// Copyright 2026 - Hewlett Packard Enterprise Company

import { useContext, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useFlags from '../../hooks/useFlags';
import { AuthContext } from '../../contexts/AuthContext';
import {
  redirectToGlcpLogin,
  saveRedirectRoute,
} from '../../utils/authHelpers';

export default function WorkspaceDeepLinkInterceptor({ children }) {
  const location = useLocation();
  const history = useHistory();
  const flags = useFlags();
  const deepLinkFlag = flags.T_20260513_122476_WORKSPACE_DEEP_LINK_BOOL();
  const { isAuthenticated, getWorkspaceId } = useContext(AuthContext);
  const redirected = useRef(false);

  const workspaceId = new URLSearchParams(location.search).get('workspace_id');

  useEffect(() => {
    if (!deepLinkFlag || !workspaceId || redirected.current) return undefined;

    let isCurrent = true;

    async function handleDeepLink() {
      const params = new URLSearchParams(location.search);

      // use case: deep link to same workspace — strip param and continue
      if (isAuthenticated()) {
        const currentWorkspaceId = await getWorkspaceId();
        if (!isCurrent) return;
        if (currentWorkspaceId === workspaceId) {
          params.delete('workspace_id');
          const remaining = params.toString();
          const newUrl = remaining
            ? `${location.pathname}?${remaining}`
            : location.pathname;
          history.replace(newUrl);
          return;
        }
      }

      redirected.current = true;

      // Save the route and remaining params for post-login redirect;
      // strip workspace_id since it is consumed by the GLCP login flow
      params.delete('workspace_id');
      saveRedirectRoute(location.pathname, params.toString());

      redirectToGlcpLogin({ workspaceId, deepLinkFlag });
    }

    handleDeepLink();

    return () => {
      isCurrent = false;
    };
  }, [
    deepLinkFlag,
    workspaceId,
    isAuthenticated,
    getWorkspaceId,
    history,
    location.pathname,
    location.search,
  ]);

  // While intercepting or redirecting, render nothing
  if (deepLinkFlag && workspaceId) {
    return null;
  }

  return children;
}
